import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  ButtonBase,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';

import SearchIcon from '@mui/icons-material/Search';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import FilterIcon from '../../global/FilterIcon';
import { TOKENS } from '../theme';

// Spec §2 — root screen. List of distinct employees (by name), filterable
// by search + date range. The filter icon (positioned before the search
// input) opens a date-range dialog modelled on MasterReadingTable.
export default function EmployeesScreen({
  sessions,
  onSelectEmployee,
  fromDate,
  toDate,
  onDateChange,
}) {
  const [query, setQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  // Buffer the picker state so Cancel reverts cleanly.
  const [draftFrom, setDraftFrom] = useState(fromDate);
  const [draftTo, setDraftTo] = useState(toDate);

  const openFilter = () => {
    setDraftFrom(fromDate);
    setDraftTo(toDate);
    setFilterOpen(true);
  };
  const applyFilter = () => {
    if (draftFrom) onDateChange('fromDate', draftFrom);
    if (draftTo) onDateChange('toDate', draftTo);
    setFilterOpen(false);
  };

  const employees = useMemo(() => {
    const map = new Map();
    (sessions || []).forEach((s) => {
      if (!s.name) return;
      const existing = map.get(s.name) || {
        name: s.name,
        userId: s.userId,
        sessionCount: 0,
      };
      existing.sessionCount += 1;
      existing.userId = existing.userId || s.userId;
      map.set(s.name, existing);
    });
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [sessions]);

  const filtered = useMemo(() => {
    if (!query.trim()) return employees;
    const q = query.toLowerCase();
    return employees.filter((e) => e.name.toLowerCase().includes(q));
  }, [employees, query]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{ fontSize: 26, fontWeight: 700, color: TOKENS.slate900 }}
        >
          Employees
        </Typography>
        <Typography sx={{ fontSize: 15, color: TOKENS.slate500, mt: 0.25 }}>
          Pick an employee to see their sessions
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {onDateChange && (
          <Tooltip title="Filter by date">
            <IconButton
              onClick={openFilter}
              size="small"
              sx={{ border: `1px solid ${TOKENS.slate200}`, borderRadius: 1.5 }}
            >
              <FilterIcon />
            </IconButton>
          </Tooltip>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder="Search employees..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: TOKENS.slate400, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Dialog
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        aria-labelledby="employee-filter-dialog"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="employee-filter-dialog">Filters</DialogTitle>
        <DialogContent>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            {/* xs: stack vertically (narrow phones); sm+: side-by-side on one row */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                pt: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                '& > *': { flex: 1, minWidth: 0 },
              }}
            >
              <KeyboardDatePicker
                autoOk
                fullWidth
                variant="inline"
                inputVariant="outlined"
                label="From Date"
                format="yyyy-MM-dd"
                value={draftFrom}
                onChange={(d) => {
                  if (!d || d.toString() === 'Invalid Date') return;
                  setDraftFrom(new Date(d));
                }}
              />
              <KeyboardDatePicker
                autoOk
                fullWidth
                variant="inline"
                inputVariant="outlined"
                label="To Date"
                format="yyyy-MM-dd"
                value={draftTo}
                onChange={(d) => {
                  if (!d || d.toString() === 'Invalid Date') return;
                  setDraftTo(new Date(d));
                }}
              />
            </Box>
          </MuiPickersUtilsProvider>
        </DialogContent>
        <DialogActions sx={{ pr: 2, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setFilterOpen(false)}
            style={{
              width: '100px',
              marginRight: '16px',
              marginBottom: '16px',
            }}
          >
            Cancel
          </Button>
          <Button
            autoFocus
            variant="contained"
            onClick={applyFilter}
            color="primary"
            style={{
              width: '100px',
              marginRight: '16px',
              marginBottom: '16px',
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          background: '#fff',
          border: `1px solid ${TOKENS.slate200}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {filtered.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 6,
              color: TOKENS.slate400,
              fontSize: 13,
            }}
          >
            No employees found.
          </Box>
        )}
        {filtered.map((e, i) => (
          <ButtonBase
            key={e.name + i}
            onClick={() => onSelectEmployee(e)}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              borderBottom:
                i < filtered.length - 1
                  ? `1px solid ${TOKENS.slate100}`
                  : 'none',
              textAlign: 'left',
              justifyContent: 'flex-start',
            }}
          >
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: TOKENS.blue100,
                color: TOKENS.blue700,
                fontWeight: 700,
                fontSize: 18,
                mr: 1.5,
              }}
            >
              {e.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{ fontSize: 16, fontWeight: 700, color: TOKENS.slate900 }}
                noWrap
              >
                {e.name}
              </Typography>
              <Typography sx={{ fontSize: 13, color: TOKENS.slate500 }}>
                {e.sessionCount} {e.sessionCount === 1 ? 'session' : 'sessions'}
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: TOKENS.slate400 }} />
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}
